import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { projectService } from "../services/projectService";
import { getStoredClientId } from "../utils/tokenUtils";
import { useProjectDashboardStats } from "../hooks/useProjectDashboardStats";
import {
  FileText,
  Play,
  CheckCircle2,
  TrendingUp,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { CardSkeleton } from "../components/skeletons/CardSkeleton";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const ProjectDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [error, setError] = useState("");

  const {
    data: dashboardResponse,
    isFetching: dashboardFetching,
    isError: dashboardQueryError,
    error: dashboardQueryErr,
  } = useProjectDashboardStats(selectedProjectId);

  const dashboardData =
    dashboardResponse?.success && dashboardResponse?.data != null
      ? dashboardResponse.data
      : null;

  const dashboardBizError =
    dashboardResponse && dashboardResponse.success === false
      ? dashboardResponse.message || "Failed to load dashboard data"
      : "";

  const dashboardErrMessage = dashboardQueryError
    ? dashboardQueryErr?.message || "Failed to load dashboard data"
    : "";

  // Load projects
  const loadProjects = useCallback(async () => {
    try {
      const urlClientId = searchParams.get("client_id");
      const storedClientId = getStoredClientId();
      const effectiveClientId =
        urlClientId || storedClientId || user?.client_id;

      if (!effectiveClientId) {
        setError("Client ID is required");
        return;
      }

      let response;
      if (user?.role === "chief-admin" && urlClientId) {
        response = await projectService.getProjects({
          page: 1,
          limit: 100,
          client_id: urlClientId,
        });
      } else {
        response = await projectService.getUserProjects(effectiveClientId);
      }

      if (response.success) {
        const projectsList = response.data?.projects || response.data || [];
        setProjects(projectsList);

        // Auto-select first project or project from URL
        const urlProjectId = searchParams.get("project_id");
        if (
          urlProjectId &&
          projectsList.find((p) => p.project_id === urlProjectId)
        ) {
          setSelectedProjectId(urlProjectId);
        } else if (projectsList.length > 0) {
          setSelectedProjectId(projectsList[0].project_id);
        }
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      setError("Failed to load projects");
    }
  }, [user?.role, user?.client_id, searchParams]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);

    // Update URL
    const clientId =
      searchParams.get("client_id") || getStoredClientId() || user?.client_id;
    navigate(`/dashboard?project_id=${projectId}&client_id=${clientId}`, {
      replace: true,
    });
  };

  // Prepare chart data
  const progressChartData = dashboardData?.progress || [];
  const statusData = dashboardData?.statusBreakdown
    ? [
        {
          name: "Passed",
          value: dashboardData.statusBreakdown.passed,
          color: "#10b981",
        },
        {
          name: "Failed",
          value: dashboardData.statusBreakdown.failed,
          color: "#ef4444",
        },
        {
          name: "Blocked",
          value: dashboardData.statusBreakdown.blocked,
          color: "#f59e0b",
        },
        {
          name: "Skipped",
          value: dashboardData.statusBreakdown.skipped,
          color: "#6b7280",
        },
        {
          name: "Pending",
          value: dashboardData.statusBreakdown.pending,
          color: "#3b82f6",
        },
        {
          name: "Untested",
          value: dashboardData.statusBreakdown.untested,
          color: "#9ca3af",
        },
      ].filter((item) => item.value > 0)
    : [];

  return (
    <div className="p-4 sm:p-6 h-screen overflow-auto flex flex-col">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
          Project Dashboard
        </h1>
        <div className="w-full sm:w-auto min-w-[250px]">
          <Label htmlFor="project-select">Select Project</Label>
          <Select
            value={selectedProjectId || ""}
            onValueChange={handleProjectChange}
          >
            <SelectTrigger id="project-select" className="mt-1">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(error || dashboardBizError || dashboardErrMessage) && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error || dashboardBizError || dashboardErrMessage}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {!selectedProjectId ? (
          <Card className="p-8 text-center">
            <p className="text-lg text-text-secondary">
              Please select a project to view dashboard
            </p>
          </Card>
        ) : dashboardFetching && !dashboardData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : dashboardData ? (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
              <Card className="p-4 sm:p-6">
                <div className="flex items-center mb-2">
                  <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                    Test Cases
                  </h3>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-text-primary">
                  {dashboardData.testCases?.total || 0}
                </div>
                <p className="text-xs sm:text-sm text-text-secondary mt-1">
                  {dashboardData.testCases?.testSuites || 0} Test Suites
                </p>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex items-center mb-2">
                  <Play className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                    Test Runs
                  </h3>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-text-primary">
                  {dashboardData.testRuns?.total || 0}
                </div>
                <p className="text-xs sm:text-sm text-text-secondary mt-1">
                  {dashboardData.testRuns?.active || 0} Active Test Runs
                </p>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex items-center mb-2">
                  <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                    Status
                  </h3>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-text-primary">
                  {dashboardData.status?.percentage || 0}%
                </div>
                <p className="text-xs sm:text-sm text-text-secondary mt-1">
                  {dashboardData.status?.passed || 0} /{" "}
                  {dashboardData.status?.total || 0}
                </p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2 sm:h-3">
                  <div
                    className="bg-primary-600 h-2 sm:h-3 rounded-full transition-all"
                    style={{
                      width: `${dashboardData.status?.percentage || 0}%`,
                    }}
                  />
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex items-center mb-2">
                  <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary">
                    Progress
                  </h3>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-text-primary">
                  {progressChartData.length > 0
                    ? `${Math.round(((progressChartData[progressChartData.length - 1]?.passed || 0) / (dashboardData.status?.total || 1)) * 100)}%`
                    : "0%"}
                </div>
                <p className="text-xs sm:text-sm text-text-secondary mt-1">
                  Last 30 days
                </p>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Progress Chart */}
              <div className="lg:col-span-2">
                <Card className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">
                    Progress
                  </h3>
                  {progressChartData.length > 0 ? (
                    <div className="w-full h-[250px] sm:h-[300px] overflow-auto">
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                        minHeight={250}
                      >
                        <LineChart data={progressChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;
                            }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: "0.875rem" }} />
                          <Line
                            type="monotone"
                            dataKey="remaining"
                            stroke="#3b82f6"
                            name="Remaining"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="passed"
                            stroke="#10b981"
                            name="Passed"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[250px] sm:h-[300px] flex items-center justify-center">
                      <p className="text-text-secondary">
                        No progress data available
                      </p>
                    </div>
                  )}
                </Card>
              </div>

              {/* Status Chart */}
              <div>
                <Card className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">
                    Status
                  </h3>
                  {statusData.length > 0 ? (
                    <>
                      <div className="w-full h-[200px] sm:h-[250px] overflow-auto">
                        <ResponsiveContainer
                          width="100%"
                          height="100%"
                          minHeight={200}
                        >
                          <PieChart>
                            <Pie
                              data={statusData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) =>
                                `${name}: ${(percent * 100).toFixed(0)}%`
                              }
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {statusData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.color}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 space-y-2">
                        {statusData.map((item) => (
                          <div key={item.name} className="flex items-center">
                            <div
                              className="w-4 h-4 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: item.color }}
                            />
                            <p className="text-xs sm:text-sm text-text-primary">
                              {item.name}: {item.value} (
                              {Math.round(
                                (item.value /
                                  (dashboardData.status?.total || 1)) *
                                  100,
                              )}
                              %)
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="h-[200px] sm:h-[250px] flex items-center justify-center">
                      <p className="text-text-secondary">
                        No status data available
                      </p>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectDashboardPage;
